// The site's languages and the URLs they live at. English is the default
// and keeps the root; every other language is a subdirectory named by its
// lower-case BCP 47 tag (/es/, /pt-br/, /zh-hans/), the structure Google
// recommends for one domain with several languages. Nothing redirects by
// language: search engines find each version through hreflang, people
// through the language links in the footer. Chinese is Simplified (zh-Hans,
// a script tag Google's hreflang accepts), for mainland China, Singapore
// and Malaysia.
//
// Shared by the build (scripts/site/i18n.js), the pages' scripts and the
// viewer. Adding a language: add it here, add src/i18n/locales/<code>/ with
// every namespace of en, run `npm run i18n:check`. See docs/i18n.md.

export const DEFAULT_LOCALE = 'en';

// code: the BCP 47 tag (resources folder, <html lang>, Intl).
// prefix: the URL directory ('' for the default).
// hreflang: the value for <link rel="alternate" hreflang>.
// og: Open Graph's language_TERRITORY form.
// name: the language's name in itself, for the language links.
export const LOCALES = [
  { code: 'en', prefix: '', hreflang: 'en', og: 'en_US', name: 'English' },
  { code: 'es', prefix: 'es', hreflang: 'es', og: 'es_ES', name: 'Español' },
  { code: 'pt-BR', prefix: 'pt-br', hreflang: 'pt-BR', og: 'pt_BR', name: 'Português (Brasil)' },
  { code: 'hi', prefix: 'hi', hreflang: 'hi', og: 'hi_IN', name: 'हिन्दी' },
  { code: 'zh-Hans', prefix: 'zh-hans', hreflang: 'zh-Hans', og: 'zh_CN', name: '简体中文' },
  { code: 'id', prefix: 'id', hreflang: 'id', og: 'id_ID', name: 'Bahasa Indonesia' },
];

export const LOCALE_CODES = LOCALES.map((l) => l.code);

export function getLocale(code) {
  return LOCALES.find((l) => l.code === code) || LOCALES[0];
}

// '/universe/' in 'es' -> '/es/universe/'. path is a site path with its
// leading slash; a fragment or query stays where it is.
export function localePath(code, path = '/') {
  const { prefix } = getLocale(code);
  return prefix ? `/${prefix}${path}` : path;
}

// '/pt-br/universe/' -> { locale: 'pt-BR', path: '/universe/' }.
export function splitLocalePath(pathname) {
  const m = /^\/([a-z]{2}(?:-[a-z0-9]{2,8})?)(\/.*)?$/i.exec(pathname || '/');
  const hit = m && LOCALES.find((l) => l.prefix && l.prefix === m[1].toLowerCase());
  return hit ? { locale: hit.code, path: m[2] || '/' } : { locale: DEFAULT_LOCALE, path: pathname || '/' };
}

// The best of `available` for a list of requested tags (navigator.languages,
// Accept-Language), by RFC 4647 lookup: each requested tag, then the same
// tag with its last subtag dropped, until one is available. A bare language
// that is not available as such takes its first regional variant ('pt'
// finds 'pt-BR'). Tags are compared after Intl's canonicalisation, so
// 'PT-br' and 'pt_BR'-style input from older browsers resolve the same.
export function negotiateLocale(requested, available = LOCALE_CODES, fallback = DEFAULT_LOCALE) {
  const canon = (tag) => {
    try { return Intl.getCanonicalLocales(String(tag).replace(/_/g, '-'))[0]; } catch { return null; }
  };
  const have = new Map(available.map((code) => [canon(code).toLowerCase(), code]));
  for (const raw of [].concat(requested || [])) {
    const tag = canon(raw);
    if (!tag) continue;
    const parts = tag.toLowerCase().split('-');
    while (parts.length) {
      const key = parts.join('-');
      if (have.has(key)) return have.get(key);
      if (parts.length === 1) {
        const regional = [...have.keys()].find((k) => k.startsWith(`${key}-`));
        if (regional) return have.get(regional);
      }
      parts.pop();
    }
  }
  return fallback;
}
